/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react'
import { Form, Input, Modal, Select, message } from 'antd'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { CloseOutlined } from '@ant-design/icons'

import { Driver, TelephonePrefix } from '@types'
import { useGetTelePhonePrefixes, useUserResponseManager } from '@hooks'
import { useUser } from '@contexts'

import { StyledForm, StyledRow } from '../../../../../components/StyledComponents'

const nameValidation = {
  required: 'globals.obligatory_field',
  min: 1,
  max: 50,
  pattern: /^[A-Za-zÁÉÍÓÚáéíóúÑñÜü0-9äëïöüÄËÏÖÜ']+( [A-Za-zÁÉÍÓÚáéíóúÑñÜü0-9äëïöüÄËÏÖÜ']+)*$/,
}

const formValidations = {
  firstName: nameValidation,
  lastName: nameValidation,
  stateIdNumber: { min: 8, max: 20 },
}

type Props = {
  open: boolean
  onClose: () => void
  onAdd: (data: Driver) => Promise<unknown>
}

const AddDriverModal: React.FC<Props> = ({ open, onClose, onAdd }) => {
  const [selectedPrefix, setSelectedPrefix] = useState('54')
  const { t } = useTranslation()
  const { state: user } = useUser()
  const { getUser } = useUserResponseManager(user?.id)
  const { telephonePrefixes } = useGetTelePhonePrefixes()
  const trucksPreferred = getUser?.user?.userDetail?.carrier?.trucks
  const trailerPreferred = getUser?.user?.userDetail?.carrier?.trailers
  const drivers = getUser?.user?.userDetail?.carrier?.drivers
  const { Option } = Select
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  const selectedTelephonePrefix = useMemo(
    () => telephonePrefixes?.find(({ prefix }: TelephonePrefix) => prefix === selectedPrefix),
    [selectedPrefix, telephonePrefixes],
  )

  const minTelephoneLength = useMemo(
    () => selectedTelephonePrefix?.min ?? 0,
    [selectedTelephonePrefix],
  )
  const maxTelephoneLength = useMemo(
    () => selectedTelephonePrefix?.min ?? 100,
    [selectedTelephonePrefix],
  )

  const validationSchema = yup.object().shape({
    firstName: yup
      .string()
      .required(t(formValidations.firstName.required))
      .min(
        formValidations.firstName.min,
        t('tpl_router.tpl_dashboard.driver_validations.name_min', {
          min: formValidations.firstName.min,
        }),
      )
      .max(
        formValidations.firstName.max,
        t('tpl_router.tpl_dashboard.driver_validations.name_max', {
          min: formValidations.firstName.max,
        }),
      )
      .matches(
        formValidations.firstName.pattern,
        t('tpl_router.tpl_dashboard.driver_validations.must_contain'),
      )
      .test(
        'no-only-spaces',
        t('tpl_router.tpl_dashboard.driver_validations.firstname_not_just_spaces'),
        value => {
          if (value?.trim() === '') {
            throw new yup.ValidationError(
              t('tpl_router.tpl_dashboard.driver_validations.firstname_not_just_spaces'),
              value,
              'firstName',
            )
          }
          return true
        },
      ),
    lastName: yup
      .string()
      .required(t(formValidations.lastName.required))
      .min(
        formValidations.lastName.min,
        t('tpl_router.tpl_dashboard.driver_validations.name_min', {
          min: formValidations.lastName.min,
        }),
      )
      .max(
        formValidations.lastName.max,
        t('tpl_router.tpl_dashboard.driver_validations.name_max', {
          max: formValidations.lastName.max,
        }),
      )
      .matches(
        formValidations.lastName.pattern,
        t('tpl_router.tpl_dashboard.driver_validations.must_contain'),
      )
      .test(
        'no-only-spaces',
        t('tpl_router.tpl_dashboard.driver_validations.lastname_not_just_spaces'),
        value => {
          if (value?.trim() === '') {
            throw new yup.ValidationError(
              t('tpl_router.tpl_dashboard.driver_validations.lastname_not_just_spaces'),
              value,
              'lastName',
            )
          }
          return true
        },
      ),
    stateIdNumber: yup
      .string()
      .required(t('globals.obligatory_field'))
      .min(
        formValidations.stateIdNumber.min,
        t('tpl_router.tpl_dashboard.driver_validations.allowed_character_range', {
          min: formValidations.stateIdNumber.min,
          max: formValidations.stateIdNumber.max,
        }),
      )
      .max(
        formValidations.stateIdNumber.max,
        t('tpl_router.tpl_dashboard.driver_validations.allowed_character_range', {
          min: formValidations.stateIdNumber.min,
          max: formValidations.stateIdNumber.max,
        }),
      )
      .test(
        'no-only-spaces',
        t('tpl_router.tpl_dashboard.driver_validations.id_not_just_spaces'),
        value => {
          if (value?.trim() === '') {
            throw new yup.ValidationError(
              t('tpl_router.tpl_dashboard.driver_validations.id_not_just_spaces'),
              value,
              'stateIdNumber',
            )
          }
          return true
        },
      ),
    phone: yup
      .string()
      .required(t('globals.obligatory_field'))
      .min(
        minTelephoneLength,
        t('tpl_router.tpl_dashboard.driver_validations.phone_required_single', {
          length: minTelephoneLength,
        }),
      )
      .max(
        maxTelephoneLength,
        t('tpl_router.tpl_dashboard.driver_validations.phone_required_single', {
          length: maxTelephoneLength,
        }),
      )
      .test(
        'no-only-spaces',
        t('tpl_router.tpl_dashboard.driver_validations.phone_number_not_just_spaces'),
        value => {
          if (value?.trim() === '') {
            throw new yup.ValidationError(
              t('tpl_router.tpl_dashboard.driver_validations.phone_number_not_just_spaces'),
              value,
              'phone',
            )
          }
          return true
        },
      ),
  })

  const { reset, handleSubmit, register, setValue, setError, formState } = useForm<{
    firstName: string
    lastName: string
    stateIdNumber: string
    phone: string
    preferredTruck: { id: unknown }
    preferredTrailer: { id: unknown }
  }>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  })

  const saveDriver = (data: Driver) => {
    setIsSubmitting(true)
    setApiError(null)

    onAdd(data)
      .then(() => {
        reset()
      })
      .catch(error => {
        handleAPIError(error)
      })
  }

  const handleAPIError = (error: any) => {
    setIsSubmitting(false)
    let errorMessage = t('tpl_router.tpl_dashboard.driver_validations.error_saving_data')
    if (error?.data?.error) {
      errorMessage = error.data.error.message || errorMessage
    } else if (error?.message) {
      errorMessage = error.message
    }
    setApiError(errorMessage)
    if (errorMessage.includes('StateIdNumber already exist')) {
      setError('stateIdNumber', {
        type: 'manual',
        message: t('tpl_router.tpl_dashboard.driver_validations.id_already_registered'),
      })
    } else if (errorMessage.includes('Telephone already exist')) {
      setError('phone', {
        type: 'manual',
        message: t('tpl_router.tpl_dashboard.driver_validations.drivers_phone_already_exists'),
      })
    } else if (errorMessage.includes('Driver already exists')) {
      setError('stateIdNumber', {
        type: 'manual',
        message: t('tpl_router.tpl_dashboard.driver_validations.driver_already_exists'),
      })
    }
  }

  useEffect(() => {
    let timeout: NodeJS.Timeout

    if (isSubmitting) {
      timeout = setTimeout(() => {
        setIsSubmitting(false)
      }, 5000)
    }

    return () => {
      if (timeout) clearTimeout(timeout)
    }
  }, [isSubmitting])

  const handleOk = () => {
    setApiError(null)
    handleSubmit(data => {
      saveDriver(data as Driver)
    })()
  }

  return (
    <Modal
      open={open}
      keyboard
      maskClosable={false}
      onCancel={() => {
        reset()
        setApiError(null)
        onClose()
      }}
      title={t('tpl_router.tpl_dashboard.add_driver_modal.title')}
      closeIcon={<CloseOutlined />}
      okText={t('tpl_router.tpl_dashboard.add_driver_modal.button_add')}
      cancelText={t('tpl_router.tpl_dashboard.add_driver_modal.button_cancel')}
      onOk={handleOk}
      confirmLoading={isSubmitting}
      destroyOnClose
    >
      <StyledForm>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
              {
                min: formValidations.firstName.min,
              },
              {
                max: formValidations.firstName.max,
              },
            ]}
            label={t('tpl_router.tpl_dashboard.add_driver_modal.firstName')}
            name="firstName"
            hasFeedback
            validateStatus={formState.errors.firstName ? 'error' : ''}
            help={formState.errors.firstName?.message}
          >
            <Input
              {...register('firstName')}
              onChange={e => {
                setValue('firstName', e.target.value)
              }}
              maxLength={formValidations.firstName.max}
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
              {
                min: formValidations.lastName.min,
              },
              {
                max: formValidations.lastName.max,
              },
            ]}
            label={t('tpl_router.tpl_dashboard.add_driver_modal.lastName')}
            name="lastName"
            hasFeedback
            validateStatus={formState.errors.lastName ? 'error' : ''}
            help={formState.errors.lastName?.message}
          >
            <Input
              {...register('lastName')}
              onChange={e => {
                setValue('lastName', e.target.value)
              }}
              maxLength={formValidations.lastName.max}
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
              {
                min: formValidations.stateIdNumber.min,
              },
              {
                max: formValidations.stateIdNumber.max,
              },
            ]}
            label={t('tpl_router.tpl_dashboard.add_driver_modal.stateIdNumber')}
            name="stateIdNumber"
            hasFeedback
            validateStatus={formState.errors.stateIdNumber ? 'error' : ''}
            help={formState.errors.stateIdNumber?.message}
          >
            <Input
              {...register('stateIdNumber')}
              onChange={e => {
                setValue('stateIdNumber', e.target.value)
              }}
              maxLength={formValidations.stateIdNumber.max}
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
            ]}
            label={t('tpl_router.tpl_dashboard.add_driver_modal.phone')}
            name="phone"
            hasFeedback
            validateStatus={formState.errors.phone ? 'error' : ''}
            help={formState.errors.phone?.message}
          >
            <Input
              addonBefore={
                <Select
                  defaultValue="54"
                  style={{ width: 100 }}
                  onChange={value => {
                    setSelectedPrefix(value)
                    setValue('phone', `${value}`)
                  }}
                >
                  {telephonePrefixes?.map((prefix: { prefix: string }) => (
                    <Option key={prefix.prefix} value={prefix.prefix}>
                      {`+${prefix.prefix}${prefix.prefix === '54' ? ' (AR)' : ''}`}
                    </Option>
                  ))}
                </Select>
              }
              {...register('phone')}
              maxLength={15}
              onChange={e => {
                const enteredValue = e.target.value
                const fullPhoneNumber = `${selectedPrefix}${enteredValue}`
                setValue('phone', fullPhoneNumber)
              }}
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <Form.Item
            label={t('tpl_router.tpl_dashboard.add_driver_modal.preferredTruck')}
            name="preferredTruck"
            hasFeedback
            validateStatus={formState.errors.preferredTruck ? 'error' : ''}
            help={formState.errors.preferredTruck?.message}
          >
            <Select
              showSearch
              allowClear
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
              placeholder={t('globals.placeholder_select')}
              filterOption={(input, option) => {
                const truck = trucksPreferred?.find(tr => tr.id === option?.value)
                if (truck) {
                  const labelTruck = `${truck.licensePlate} ${truck.description}${
                    truck.equipmentType ? ` ${truck.equipmentType.name}` : ''
                  }`.toLowerCase()
                  return labelTruck.includes(input.toLowerCase())
                }
                return false
              }}
              options={trucksPreferred
                ?.filter(truck => !truck.disableAt)
                .map(truck => ({
                  label: `${truck.licensePlate} - ${truck.description}${
                    truck.equipmentType ? ` - ${truck.equipmentType.name}` : ''
                  }`,
                  value: truck.id,
                }))}
              onChange={value => {
                setValue('preferredTruck', { id: value })
              }}
            />
          </Form.Item>
          <Form.Item
            label={t('tpl_router.tpl_dashboard.edit_driver_modal.preferredTrailer')}
            name="preferredTrailer"
            hasFeedback
            validateStatus={formState.errors.preferredTrailer ? 'error' : ''}
            help={formState.errors.preferredTrailer?.message}
          >
            <Select
              showSearch
              allowClear
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
              placeholder={t('globals.placeholder_select')}
              filterOption={(input, option) => {
                const trailer = trailerPreferred?.find(tr => tr.id === option?.value)
                if (trailer) {
                  const labelTrailer = `${trailer.licensePlate} ${trailer.description}${
                    trailer.equipmentType ? ` ${trailer.equipmentType.name}` : ''
                  }`.toLowerCase()

                  return labelTrailer.includes(input.toLowerCase())
                }
                return false
              }}
              options={trailerPreferred
                ?.filter(trailer => !trailer.disableAt)
                .map(trailer => ({
                  label: `${trailer.licensePlate} - ${trailer.description}${
                    trailer.equipmentType ? ` - ${trailer.equipmentType.name}` : ''
                  }`,
                  value: trailer.id,
                }))}
              onChange={value => {
                setValue('preferredTrailer', { id: value })
              }}
            />
          </Form.Item>
        </StyledRow>
      </StyledForm>
    </Modal>
  )
}

export { AddDriverModal }
